import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaTimes, FaCopy, FaShareAlt, FaCheck, FaWhatsapp } from 'react-icons/fa';
import { getProdDb, getCurrentUserEnv } from '@services/firebase';
import { getDocs, getDoc, collection, doc } from 'firebase/firestore';
import { useAlert } from '../contexts/ToastContext';

const ROLES = ['Buyer', 'User', 'Mover'];
const ENVS = ['Prod', 'Test'];
const VALIDITY_OPTS = [5, 10, 15, 30, 60];

const AppShare = ({ userRole: propUserRole }) => {
  const navigate = useNavigate();
  const showAlert = useAlert();

  const [userRole] = useState(() => {
    if (propUserRole) return propUserRole;
    try { return localStorage.getItem('userRole') || ''; } catch { return ''; }
  });

  // Order email search
  const [orderEmails, setOrderEmails] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  // Invite target
  const [emailInput, setEmailInput] = useState('');
  const [selectedName, setSelectedName] = useState('');

  // Link config
  const [role, setRole] = useState('Buyer');
  const [env, setEnv] = useState('Prod');
  const [validity, setValidity] = useState(5);

  // Output
  const [generatedLink, setGeneratedLink] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';

  useEffect(() => {
    const load = async () => {
      try {
        const db = getProdDb();
        const [snap1, snap2] = await Promise.all([
          getDocs(collection(db, 'FormOrders')),
          getDocs(collection(db, 'FormOrderOld')),
        ]);
        const seen = new Set();
        const list = [];
        [...snap1.docs, ...snap2.docs].forEach(d => {
          const data = d.data();
          const email = (data['E-Mail-Address'] || '').trim().toLowerCase();
          if (email && !seen.has(email)) {
            seen.add(email);
            list.push({ email, name: (data['Name/Email'] || '').trim() });
          }
        });
        list.sort((a, b) => a.email.localeCompare(b.email));
        setOrderEmails(list);
      } catch (_) {}
    };
    load();
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) { setSearchResults([]); setShowDropdown(false); return; }
    const q = searchQuery.trim().toLowerCase();
    const results = orderEmails.filter(o =>
      o.email.includes(q) || o.name.toLowerCase().includes(q)
    ).slice(0, 8);
    setSearchResults(results);
    setShowDropdown(results.length > 0);
  }, [searchQuery, orderEmails]);

  const pickEmail = (o) => {
    setEmailInput(o.email);
    setSelectedName(o.name);
    setSearchQuery('');
    setSearchResults([]);
    setShowDropdown(false);
    setGeneratedLink('');
  };

  const clearEmail = () => {
    setEmailInput('');
    setSelectedName('');
    setSearchQuery('');
    setGeneratedLink('');
  };

  const resetForm = () => {
    setGeneratedLink('');
    setCopied(false);
  };

  const generate = async () => {
    const email = emailInput.trim().toLowerCase();
    setIsGenerating(true);
    setGeneratedLink('');

    try {
      if (email) {
        // Check if user already exists in profile collection
        const profileSnap = await getDocs(collection(getProdDb(), 'profile'));
        const profileDoc = profileSnap.docs.find(
          d => (d.data().email || '').trim().toLowerCase() === email
        );
        if (profileDoc) {
          let msg = 'User Already Signed Up.';
          try {
            const cUsersSnap = await getDoc(doc(getProdDb(), 'cUsers', profileDoc.id));
            const isFirstLogin = cUsersSnap.exists() && cUsersSnap.data().first_login === true;
            if (isFirstLogin) {
              const pendingSnap = await getDoc(doc(getProdDb(), 'pendingSecurityCodes', email));
              if (pendingSnap.exists() && pendingSnap.data().securityCode) {
                msg += ` Reference Code: ${pendingSnap.data().securityCode}`;
              }
            }
          } catch (_) {}
          showAlert(msg);
          setIsGenerating(false);
          return;
        }
      }

      const params = new URLSearchParams({ role, env });
      if (email) {
        params.set('em', btoa(email));
        params.set('exp', String(Date.now() + validity * 60 * 1000));
      }

      setGeneratedLink(`${window.location.origin}/signup?${params.toString()}`);
    } catch (err) {
      showAlert(`Error: ${err.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { showAlert('Could not copy to clipboard.'); }
  };

  const shareVia = (type) => {
    const msg = `Sign Up to Priyas: ${generatedLink}`;
    if (type === 'whatsapp') window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank', 'noopener,noreferrer');
    else if (type === 'sms') window.location.href = `sms:?body=${encodeURIComponent(msg)}`;
    else if (type === 'native') {
      if (navigator.share) navigator.share({ title: 'Sign Up to Priyas', url: generatedLink });
      else showAlert('Native sharing not supported. Use Copy instead.');
    }
  };

  const qrUrl = generatedLink
    ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(generatedLink)}`
    : '';

  const card = {
    background: isDark ? '#1e1e1e' : '#fff',
    borderRadius: '10px', padding: '16px', marginBottom: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  };
  const inp = {
    width: '100%', padding: '8px 10px', borderRadius: '6px',
    border: `1px solid ${isDark ? '#333' : '#ddd'}`,
    background: isDark ? '#2a2a2a' : '#fafafa',
    color: isDark ? '#eee' : '#333', fontSize: '13px',
    boxSizing: 'border-box', outline: 'none',
  };
  const pill = (active) => ({
    padding: '5px 12px', borderRadius: '20px',
    border: `1px solid ${active ? '#fa4848' : isDark ? '#444' : '#ddd'}`,
    background: active ? '#fa4848' : 'transparent',
    color: active ? '#fff' : isDark ? '#aaa' : '#666',
    fontSize: '12px', fontWeight: '600', cursor: 'pointer',
  });

  if ((userRole !== 'Admin' && userRole !== 'Super') || getCurrentUserEnv() === 'Test') {
    return <div style={{ padding: 20, textAlign: 'center', color: isDark ? '#aaa' : '#666' }}>Access denied.</div>;
  }

  return (
    <div style={{ padding: '12px', paddingBottom: '80px', maxWidth: '480px', margin: '0 auto' }}>

      {/* Back button only */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
        <button onClick={() => navigate('/profile')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: isDark ? '#aaa' : '#666', fontSize: '16px' }}>
          <FaTimes />
        </button>
      </div>

      {!generatedLink ? (
        <>
          {/* Single email search section */}
          <div style={card}>
            <div style={{ position: 'relative', marginBottom: emailInput ? '10px' : 0 }}>
              <FaSearch style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#999', fontSize: '11px', pointerEvents: 'none' }} />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); if (!e.target.value) clearEmail(); }}
                onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
                onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                style={{ ...inp, paddingLeft: '28px' }}
              />
              {showDropdown && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
                  background: isDark ? '#252525' : '#fff',
                  border: `1px solid ${isDark ? '#333' : '#ddd'}`,
                  borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', overflow: 'hidden',
                }}>
                  {searchResults.map(o => (
                    <div
                      key={o.email}
                      onMouseDown={() => pickEmail(o)}
                      style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: `1px solid ${isDark ? '#333' : '#f0f0f0'}` }}
                      onMouseEnter={e => e.currentTarget.style.background = isDark ? '#333' : '#f5f5f5'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      {o.name && <div style={{ fontSize: '13px', fontWeight: '600', color: isDark ? '#eee' : '#333' }}>{o.name}</div>}
                      <div style={{ fontSize: '11px', color: '#888' }}>{o.email}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {emailInput && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: isDark ? '#2a2a2a' : '#f5f5f5', borderRadius: '6px', padding: '6px 10px' }}>
                <div>
                  {selectedName && <div style={{ fontSize: '12px', fontWeight: '600', color: isDark ? '#eee' : '#333' }}>{selectedName}</div>}
                  <div style={{ fontSize: '12px', color: '#888' }}>{emailInput}</div>
                </div>
                <button onClick={clearEmail} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', padding: '2px' }}>
                  <FaTimes size={11} />
                </button>
              </div>
            )}
          </div>

          {/* Role + Env + Validity */}
          <div style={card}>
            <div style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {ROLES.map(r => <button key={r} style={pill(role === r)} onClick={() => setRole(r)}>{r}</button>)}
              </div>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', gap: '6px' }}>
                {ENVS.map(e => <button key={e} style={pill(env === e)} onClick={() => setEnv(e)}>{e}</button>)}
              </div>
            </div>
            <div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {VALIDITY_OPTS.map(v => <button key={v} style={pill(validity === v)} onClick={() => setValidity(v)}>{v} min</button>)}
              </div>
            </div>
          </div>

          {/* Generate button */}
          <button
            onClick={generate}
            disabled={isGenerating}
            style={{
              width: '100%', padding: '12px', borderRadius: '8px', marginBottom: '14px',
              background: isGenerating ? '#ccc' : '#fa4848', color: '#fff',
              border: 'none', cursor: isGenerating ? 'not-allowed' : 'pointer',
              fontWeight: '700', fontSize: '14px',
            }}
          >
            {isGenerating ? 'Generating...' : '🔗 Generate'}
          </button>
        </>
      ) : (
        /* Post-generation: QR + share buttons only */
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: '18px' }}>
            <img
              src={qrUrl}
              alt="Signup QR Code"
              style={{ width: 220, height: 220, borderRadius: '12px', border: '4px solid #fff', boxShadow: '0 2px 16px rgba(0,0,0,0.18)' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '18px' }}>
            <button
              onClick={copyLink}
              style={{
                display: 'flex', alignItems: 'center', gap: '5px',
                padding: '10px 18px', borderRadius: '6px', fontSize: '13px', fontWeight: '600',
                background: copied ? '#22c55e' : isDark ? '#333' : '#f0f0f0',
                color: copied ? '#fff' : isDark ? '#eee' : '#333', border: 'none', cursor: 'pointer',
              }}
            >
              {copied ? <FaCheck size={11} /> : <FaCopy size={11} />} {copied ? 'Copied!' : 'Copy'}
            </button>
            <button
              onClick={() => shareVia('whatsapp')}
              style={{
                display: 'flex', alignItems: 'center', gap: '5px',
                padding: '10px 18px', borderRadius: '6px', fontSize: '13px', fontWeight: '600',
                background: '#25D366', color: '#fff', border: 'none', cursor: 'pointer',
              }}
            >
              <FaWhatsapp size={14} /> WhatsApp
            </button>
            <button
              onClick={() => shareVia('sms')}
              style={{
                display: 'flex', alignItems: 'center', gap: '5px',
                padding: '10px 18px', borderRadius: '6px', fontSize: '13px', fontWeight: '600',
                background: isDark ? '#333' : '#f0f0f0', color: isDark ? '#eee' : '#333',
                border: 'none', cursor: 'pointer',
              }}
            >
              💬 SMS
            </button>
            {!!navigator.share && (
              <button
                onClick={() => shareVia('native')}
                style={{
                  display: 'flex', alignItems: 'center', gap: '5px',
                  padding: '10px 18px', borderRadius: '6px', fontSize: '13px', fontWeight: '600',
                  background: isDark ? '#333' : '#f0f0f0', color: isDark ? '#eee' : '#333',
                  border: 'none', cursor: 'pointer',
                }}
              >
                <FaShareAlt size={11} /> Share
              </button>
            )}
          </div>

          <button
            onClick={resetForm}
            style={{
              background: 'none', border: `1px solid ${isDark ? '#444' : '#ddd'}`,
              borderRadius: '6px', padding: '8px 20px', cursor: 'pointer',
              fontSize: '12px', color: isDark ? '#aaa' : '#666',
            }}
          >
            Generate New
          </button>
        </div>
      )}
    </div>
  );
};

export default AppShare;
